import React, { useEffect, useState } from "react";
import CmsApplicationsFilter from "./component/CmsApplicationsFilter";
import { useAppDispatch, useAppSelector } from "store";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { applicationsLimits } from "configs/limits";
import { CmsApplicationsHeader } from "./component/CmsApplicationsHeader";
import Pagination from "components/Pagination";
import { Table } from "components/table/Table";
import {
  Application,
  handleSelectAll as onSelectAll,
  handlePaginate as onPaginate,
  handleSelect as onSelect,
  getApplicationsList,
  deleteApplicationItem,
  bunchUpdateApplications,
} from "store/cms/footer/applications/slice";
import { ModalTypes } from "configs/types";
import { getIsActiveNumeric } from "helpers/getIsActiveNumeric";
import { SortOrder, updateSort } from "helpers/updateSort";
import { openModal } from "store/modals/slice";
import EmptyState from "components/EmptyState/EmptyState";
import DataTableSkeleton from "components/global/Skeleton/TableSkeleton/DataTableSkeleton";
import PaginationSkeleton from "components/global/Skeleton/PaginationSkeleton/PaginationSkeleton";
import { usePermissionHelper } from "helpers/usePermissionHelper";
import { useRefreshList } from "helpers/useRefreshList";

const ApplicationsList: React.FC = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const [limit, setLimit] = useState<number>(applicationsLimits[0]);
  const [filterByTitle, setFilterByTitle] = useState<string>("");
  const [filterByGeo, setFilterByGeo] = useState<
    { value: string | number; label: string }[]
  >([]);
  const [filterByActive, setFilterByActive] = useState<
    { value: string | number; label: string }[]
  >([]);
  const [order, setOrder] = useState<SortOrder<Application>>({});

  const applicationsList = useAppSelector(
    (state) => state["cms-applications"].list,
  );

  const isAppListFetched = useAppSelector(
    (state) => state["cms-applications"].isListFetched,
  );

  const totalItems = useAppSelector(
    (state) => state["cms-applications"].totalItems,
  );
  const totalPages = useAppSelector(
    (state) => state["cms-applications"].totalPages,
  );
  const activePage = useAppSelector(
    (state) => state["cms-applications"].activePage,
  );
  const selectedItems = useAppSelector(
    (state) => state["cms-applications"].selectedItems,
  );
  const processingItemId = useAppSelector(
    (state) => state["cms-applications"].processingItemId,
  );

  const { refresh, isRefreshRequested } = useRefreshList({
    sliceName: 'cms-applications',
    isListFetched: isAppListFetched
  });
  
  const permissions = usePermissionHelper();

  const actionOptions = [
    { label: "Activate", value: "activate" },
    { label: "Hide", value: "hide" },
  ];

  useEffect(() => {
    if (!isAppListFetched) {
      dispatch(
        getApplicationsList({
          limit,
          offset: activePage - 1,
          title: filterByTitle,
          countries: filterByGeo.map((g) => g.value as string),
          isActive: getIsActiveNumeric(filterByActive),
          order,
        }),
      );
    }
  }, [isAppListFetched, activePage, limit]);

  const handleOrderClick = (key: keyof typeof order) => {
    setOrder((currentOrder) => updateSort(currentOrder, key));
    dispatch(onPaginate(1));
  };

  const handleSelectAll = () => {
    dispatch(onSelectAll());
  };

  const handleSelect = (id: number) => {
    dispatch(onSelect(id));
  };

  const handlePreviousClick = () => {
    if (activePage > 1) {
      dispatch(onPaginate(activePage - 1));
    }
  };

  const handleNextClick = () => {
    if (activePage < totalPages) {
      dispatch(onPaginate(activePage + 1));
    }
  };

  const handleApplyFilter = () => {
    dispatch(onPaginate(1));
  };

  const handleDelete = (id: number) => {
    if (!permissions?.delete) {
      return;
    }

    dispatch(deleteApplicationItem(id));
  };

  const handleOpenCreateModal = () => {
    navigate(`${location.pathname}?activeModal=${ModalTypes.applications}`);

    dispatch(openModal({ activeModal: ModalTypes.applications }));
  };

  const handleEditModal = (id: number) => {
    navigate(
      `${location.pathname}?activeModal=${ModalTypes.applications}&entityId=${id}`,
    );

    dispatch(openModal({ activeModal: ModalTypes.applications }));
  };

  const handleSelectAction = async (value: string) => {
    if (!selectedItems.length) return;

    const isActive = value === "activate" ? 1 : 0;

    await dispatch(
      bunchUpdateApplications({
        ids: selectedItems,
        isActive,
      }),
    );
  };

  const actionProps = permissions?.update
  ? {
      onEdit: (id: number) => handleEditModal(id),
      onView: undefined,
    }
  : {
      onEdit: undefined,
      onView: (id: number) => handleEditModal(id),
    };

  return (
    <div className="page-list-wrapper page-list--with-filter">
      <CmsApplicationsFilter
        filterByTitle={filterByTitle}
        onFilterSearchByTitle={setFilterByTitle}
        filterByActive={filterByActive}
        onFilterByActive={setFilterByActive}
        filterByGeo={filterByGeo}
        onFilterByGeo={setFilterByGeo}
        handleApply={handleApplyFilter}
      />
      <div className="page-list--with-filter-content">
        <CmsApplicationsHeader
          options={actionOptions}
          selectAction={handleSelectAction}
          createModal={handleOpenCreateModal}
          isDisabled={selectedItems.length === 0 || !permissions?.update}
          isCreateAllowed={permissions?.create}
          refresh={refresh}
          isRefreshRequested={isRefreshRequested}
        />

        {!isAppListFetched && (
          <>
            <DataTableSkeleton columnsCount={6} rowsCount={5} />
            <PaginationSkeleton />
          </>
        )}

        {isAppListFetched && applicationsList.length === 0 && (
          <div className="game-table-wrap">
            <EmptyState message={t("noApplicationsFound")} />
          </div>
        )}

        {isAppListFetched && applicationsList.length > 0 && (
          <>
            <div className="game-table-wrap">
              <Table
                data={applicationsList}
                idField="id"
                order={order}
                onSort={(v) => {
                  const k = v as unknown;
                  handleOrderClick(k as keyof typeof order);
                }}
                columns={{
                  sortingColumns: {
                    geo: {
                      value: "geo",
                      hasSorting: true,
                    },
                  },
                }}
                onAllSelect={handleSelectAll}
                onSelect={handleSelect}
                selectedItems={selectedItems}
                processingId={processingItemId}
                onDelete={handleDelete}
                hasDelete={permissions?.delete}
                hasEdit={permissions?.update}
                hasView={permissions?.read && !permissions?.update}
                hasOptions={permissions?.read || permissions?.update}
                {...actionProps}
              />
            </div>

            <Pagination
              handleLimitClick={(e) => {
                setLimit(e);
                dispatch(onPaginate(1));
              }}
              limitsArray={applicationsLimits}
              activePage={activePage}
              limit={limit}
              totalItems={totalItems}
              totalPages={totalPages}
              handleNextClick={handleNextClick}
              handlePrevClick={handlePreviousClick}
            />
          </>
        )}
      </div>
    </div>
  );
};

export default ApplicationsList;
